"use client"

import * as React from "react"
import { Suspense } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { LoginForm, LoginFormValues } from "@/components/auth/login-form"
import { useRouter, useSearchParams } from "next/navigation"

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * API response structure for login
 * Matches the response format from /api/auth/login (see app/api/auth/login/route.ts)
 */
interface LoginApiResponse {
  success: boolean
  data: {
    id: string
    email: string
    name: string
    createdAt: string
    token: string
    workspaces: Array<{
      id: string
      name: string
      slug: string
      role: string
    }>
    defaultWorkspaceId: string | null
  }
}

// ============================================================================
// Icons
// ============================================================================

/**
 * Lock icon for login page
 */
function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

// ============================================================================
// Loading Skeleton
// ============================================================================

/**
 * Loading skeleton for the login form
 */
function LoginFormSkeleton() {
  return (
    <div className="space-y-6">
      {/* Email Field Skeleton */}
      <div className="space-y-2">
        <div className="h-5 w-20 animate-pulse rounded bg-muted" />
        <div className="h-10 w-full animate-pulse rounded bg-muted" />
      </div>

      {/* Password Field Skeleton */}
      <div className="space-y-2">
        <div className="h-5 w-24 animate-pulse rounded bg-muted" />
        <div className="h-10 w-full animate-pulse rounded bg-muted" />
      </div>

      {/* Button Skeleton */}
      <div className="pt-4">
        <div className="h-10 w-full animate-pulse rounded bg-muted" />
      </div>
    </div>
  )
}

// ============================================================================
// Page Component
// ============================================================================

/**
 * Login Page Content
 *
 * Inner component that uses useSearchParams (requires Suspense boundary)
 */
function LoginPageContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const rawRedirect = searchParams.get("redirect") || "/"
  // Validate redirect to prevent open redirect attacks
  const redirectTo = rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : "/"

  // State for login operation
  const [submitting, setSubmitting] = React.useState(false)
  const [loginError, setLoginError] = React.useState<string | null>(null)
  const [loginSuccess, setLoginSuccess] = React.useState<string | null>(null)

  /**
   * Handle login operation
   */
  const handleLogin = async (values: LoginFormValues) => {
    setSubmitting(true)
    setLoginError(null)
    setLoginSuccess(null)

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || "Failed to sign in")
      }

      const result: LoginApiResponse = await response.json()

      // Store workspace selection in localStorage AND cookie
      // Cookie is needed immediately for server-side dashboard filtering on first load
      if (result.data.defaultWorkspaceId) {
        localStorage.setItem("current_workspace_id", result.data.defaultWorkspaceId)
        document.cookie = `current_workspace_id=${encodeURIComponent(result.data.defaultWorkspaceId)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
      }

      setLoginSuccess("Sign in successful! Redirecting...")

      // Redirect to original page (or dashboard) after a brief delay
      // Use window.location for full page reload to refresh auth state in Header
      setTimeout(() => {
        window.location.href = redirectTo
      }, 1000)
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : "An error occurred while signing in")
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Clear login messages (success/error)
   */
  const handleClearMessages = () => {
    setLoginError(null)
    setLoginSuccess(null)
  }

  return (
    <div className="container mx-auto max-w-md py-12">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <LockIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Sign In</CardTitle>
              <CardDescription>
                Enter your email and password to access your account
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoginForm
            initialValues={{ email: "", password: "" }}
            onSubmit={handleLogin}
            submitting={submitting}
            error={loginError}
            success={loginSuccess}
            onClearMessages={handleClearMessages}
          />
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Login Page
 *
 * Allows users to sign in with email and password.
 * On successful login, user is redirected to the dashboard.
 */
export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="container mx-auto max-w-md py-12">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <LockIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Sign In</CardTitle>
                <CardDescription>
                  Enter your email and password to access your account
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <LoginFormSkeleton />
          </CardContent>
        </Card>
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  )
}
